import {
  Context,
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import { isAdaAssetId } from "@sundae/cardano-utils";
import { useI18N } from "@sundae/react-hooks";
import { AssetAmount } from "@sundaeswap/asset";
import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";
import { DEFAULT_DECIMALS } from "../../constants/cardano.constants";
import { DEFAULT_INPUT_PLACEHOLDER } from "../../constants/exchange.constants";
import { useAdaBalance } from "../../hooks/useAdaBalance";
import { useAvailablePools } from "../../hooks/useAvailablePools";
import { useOrderMode } from "../../hooks/useExchangeMode";
import { useInputOutputAssets } from "../../hooks/useInputOutputAssets";
import { useIsWalletConnected } from "../../hooks/useIsWalletConnected";
import { useOrderType } from "../../hooks/useOrderType";
import { getCalculatedSwapRatio, getRatioValues } from "../../utils/assets.utils";
import { isSufficientQuantity } from "../../utils/exchange.utils";
import { numberI18n } from "../../utils/number-format";
import { EOrderFlowState, EOrderType, useOrderContext } from "../OrderContext";
import { OrderActions } from "../OrderContext/actions";
import { useProfileAppContext } from "../ProfileAppContext";
import { appReducer } from "./reducer";
import {
  EExchangeActions,
  ETradeWarning,
  IExchangeAction,
  IExchangeContext,
  IExchangeState,
} from "./types";

export const defaultAppState: IExchangeState = {
  activeInterval: "monthly",
  assetChangeScope: undefined,
  displayElements: undefined,
  transactionErrorMessage: undefined,
  transactionErrorHandler: undefined,
  transactionErrorActions: undefined,
  formattedLimitValue: undefined,
  formattedValueAssetA: DEFAULT_INPUT_PLACEHOLDER,
  formattedValueAssetB: DEFAULT_INPUT_PLACEHOLDER,
  outputExceedsReserve: false,
  isAssetConsentChecked: false, // needs to be set to false on every asset selection --> we don't want to preserve the checkbox state for different assets
  isInsufficientAssetA: false,
  isInsufficientAssetB: false,
  isRemainingAdaAmountBelowThreshold: false,
  limitValue: undefined,
  pageSize: 50,
  page: 0,
  parity: undefined,
  swapOutcome: undefined,
  tradeWarning: ETradeWarning.none,
  valueAssetA: "",
  valueAssetB: "",
  transactionBuilderBuiltTx: undefined,
  transactionBuilderCompleteTx: undefined,
  transactionBuilderState: undefined,
};

const defaultContext: IExchangeContext = {
  activeAssetA: undefined,
  activeAssetAId: undefined,
  activeAssetB: undefined,
  activeAssetBId: undefined,
  assetADecimals: undefined,
  assetBDecimals: undefined,
  assetsWithBalance: undefined,
  assetWarning: undefined,
  availablePools: null,
  balanceLoaded: false,
  checkInsufficientAssetA: (_value: string) => {},
  checkInsufficientAssetB: (_value: string) => {},
  dispatch: (_action: IExchangeAction) => {},
  exchangeModeFromSearch: undefined,
  exchangeOrderTypeFromSearch: undefined,
  inputAssetList: undefined,
  isFetched: false,
  isLoading: false,
  isRefetching: false,
  isValidLimitValue: false,
  isWalletConnected: false,
  outputAssetList: undefined,
  resetContext: noop,
  resetLimitOrderState: noop,
  setLimitValueToInitialRatio: noop,
  state: defaultAppState,
};

const ExchangeContext: Context<IExchangeContext> = createContext(defaultContext);

export interface IExchangeContextProviderProps {
  activeAssetAId?: string;
  activeAssetBId?: string;
  inputValue?: string;
  isDialog?: boolean;
  order?: EOrderType;
  outputValue?: string;
}

export const ExchangeContextProvider: FC<PropsWithChildren<IExchangeContextProviderProps>> = ({
  activeAssetAId,
  activeAssetBId,
  children,
  inputValue,
  isDialog,
  outputValue,
}) => {
  /**
   * @TODO The order context should not be required in ExchangeContext, but until we untangle them
   * it is required for now.
   */
  const {
    state: { pool: activePool },
    dispatch: orderDispatch,
  } = useOrderContext();
  const { currentLocale } = useI18N();
  const {
    state: { prefersReducedMotion },
  } = useProfileAppContext();
  const exchangeModeFromSearch = useOrderMode();
  const exchangeOrderTypeFromSearch = useOrderType();
  const [state, dispatch] = useReducer(appReducer, defaultAppState);
  const isWalletConnected = useIsWalletConnected();
  const { availableAdaBalance } = useAdaBalance();
  const { dispatch: orderContextDispatch } = useOrderContext();

  const {
    availablePools,
    isFetching: isLoadingAvailablePools,
    isRefetching: isRefetchingAvailablePools,
    isFetched: isFetchedAvailablePools,
  } = useAvailablePools({
    givenAssetId: activeAssetAId,
    takenAssetId: activeAssetBId,
  });

  const {
    givenAsset: activeAssetA,
    takenAsset: activeAssetB,
    assetsWithBalance,
    inputAssetList,
    isLoading,
    balanceLoaded,
    isRefetching,
    outputAssetList,
    isFetched,
  } = useInputOutputAssets({
    forcePoolsForADA: exchangeModeFromSearch.isBuyOrder,
    pageSize: state.pageSize,
    page: state.page,
    isDialog,
  });
  const isActiveAssetAAda = useMemo(() => {
    const assetId = activeAssetA?.metadata.assetId;
    return !!assetId && isAdaAssetId(assetId);
  }, [activeAssetA]);
  const isActiveAssetBAda = useMemo(() => {
    const assetId = activeAssetB?.metadata.assetId;
    return !!assetId && isAdaAssetId(assetId);
  }, [activeAssetA]);

  const contentAnimationTimeout = prefersReducedMotion ? 0 : 300;

  const resetContext = () => {
    dispatch({
      type: EExchangeActions.SET_VALUE_ASSET_A,
      payload: { valueAssetA: "" },
    });
    dispatch({
      type: EExchangeActions.SET_VALUE_ASSET_B,
      payload: { valueAssetB: "" },
    });
    dispatch({
      type: EExchangeActions.SET_FORMATTED_LIMIT_VALUE,
      payload: { formattedLimitValue: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_FORMATTED_VALUE_ASSET_A,
      payload: { formattedValueAssetA: DEFAULT_INPUT_PLACEHOLDER },
    });
    dispatch({
      type: EExchangeActions.SET_FORMATTED_VALUE_ASSET_B,
      payload: { formattedValueAssetB: DEFAULT_INPUT_PLACEHOLDER },
    });
    dispatch({
      type: EExchangeActions.SET_LIMIT_VALUE,
      payload: { limitValue: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_IS_INSUFFICIENT_A,
      payload: { isInsufficientAssetA: false },
    });
    dispatch({
      type: EExchangeActions.SET_IS_INSUFFICIENT_B,
      payload: { isInsufficientAssetB: false },
    });
    dispatch({
      type: EExchangeActions.SET_TRANSACTION_ERROR_MESSAGE,
      payload: { transactionErrorMessage: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_TRANSACTION_ERROR_HANDLER,
      payload: { transactionErrorRaw: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_TRANSACTION_ERROR_ACTIONS,
      payload: { transactionErrorActions: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_SWAP_OUTCOME,
      payload: { swapOutcome: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_PARITY,
      payload: { parity: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_OUTPUT_EXCEEDS_RESERVE,
      payload: { outputExceedsReserve: false },
    });
  };

  const assetADecimals = useMemo(
    () => activeAssetA?.metadata.decimals ?? DEFAULT_DECIMALS,
    [activeAssetA?.metadata, isLoading, isLoadingAvailablePools]
  );
  const assetBDecimals = useMemo(
    () => activeAssetB?.metadata.decimals ?? DEFAULT_DECIMALS,
    [activeAssetB?.metadata, isLoading, isLoadingAvailablePools]
  );

  const checkInsufficientAssetA = (value: string) => {
    if (isLoading || isLoadingAvailablePools) return;
    const quantity = isActiveAssetAAda ? availableAdaBalance?.quantity : activeAssetA?.quantity;
    return dispatch({
      type: EExchangeActions.SET_IS_INSUFFICIENT_A,
      payload: {
        isInsufficientAssetA: !isSufficientQuantity({
          // We are using inputValue here because when the order is updated, the funds from the original tx are used in the new one
          quantity: (quantity?.value.toNumber() ?? 0) + Number(inputValue ?? "0"),
          value,
        }),
      },
    });
  };

  const checkInsufficientAssetB = (value: string) => {
    if (isLoading || isLoadingAvailablePools) return;
    const quantity = isActiveAssetBAda ? availableAdaBalance?.quantity : activeAssetB?.quantity;
    return dispatch({
      type: EExchangeActions.SET_IS_INSUFFICIENT_B,
      payload: {
        isInsufficientAssetB: !isSufficientQuantity({
          quantity: quantity?.value.toNumber(),
          value,
        }),
      },
    });
  };

  const isValidLimitValue = useMemo(
    () => !isEmpty(state.limitValue) && Number(state.limitValue) !== 0,
    [state.limitValue]
  );

  /**
   * Sets the initial limit value to the ratio of two assets in a pool.
   *
   * This function determines the direction of the ratio based on whether the asset ID of `activeAssetA`
   * matches the asset ID of `assetA` in `activePool`. It then calculates the swap ratio and dispatches
   * actions to set both the `limitValue` and `formattedLimitValue` in the application state.
   */
  const setLimitValueToInitialRatio = () => {
    if (!activeAssetA || !activeAssetB || !activePool || !state.swapOutcome || state.limitValue) {
      return;
    }

    let display: string;
    let calculatedAmount: AssetAmount;

    /**
     * If the outputValue is 0 (meaning we are updating a swap with no minReceivable)
     * then we set it to at least 1 unit of the asset being updated (per SC rules).
     * Otherwise we calculate the ratio based on the outputValue.
     */
    if (outputValue === "0") {
      const decimals =
        activeAssetAId === activePool.assetA.assetId
          ? activePool.assetA.decimals
          : activePool.assetB.decimals;

      const decimalOffset =
        activeAssetAId === activePool.assetA.assetId
          ? activePool.assetA.decimals - activePool.assetB.decimals
          : activePool.assetB.decimals - activePool.assetA.decimals;

      display = (1 * 10 ** decimalOffset).toString();
      calculatedAmount = new AssetAmount(1, decimals);
    } else {
      // TODO: this is pretty messy; in the case of a buy order, `activeAssetA` and `activeAssetB`
      const ratioValues = getRatioValues(
        exchangeModeFromSearch.isBuyOrder ? activeAssetB.metadata : activeAssetA.metadata,
        activePool,
        state.swapOutcome,
        { inputValue: inputValue ?? "0", outputValue: outputValue ?? "0" }
      );
      const calculatedRatio = getCalculatedSwapRatio(activePool, ratioValues);

      display = calculatedRatio.display;
      calculatedAmount = calculatedRatio.calculatedAmount;
    }

    dispatch({ type: EExchangeActions.SET_LIMIT_VALUE, payload: { limitValue: display } });
    dispatch({
      type: EExchangeActions.SET_FORMATTED_LIMIT_VALUE,
      payload: {
        formattedLimitValue: numberI18n(
          calculatedAmount.value.toNumber(),
          { minimumFractionDigits: calculatedAmount.decimals },
          currentLocale
        ),
      },
    });
  };

  /**
   * This resets all state nececessary in order to perform a recalculation of the limit value.
   * Since a recalculation of the limit value is only possible when there's no limit value available yet, we have to reset it first.
   * This is because we do not want to recalculate the limit value when the user has already set a limit value.
   * Also, we will reset the `flowState` to `initial` in order to make sure the user can perform a recalculation of the limit value.
   * This is because we do not want to recalculate the limit value when the user cancels his preview or his order submission.
   */
  const resetLimitOrderState = () => {
    dispatch({
      type: EExchangeActions.SET_FORMATTED_LIMIT_VALUE,
      payload: { formattedLimitValue: undefined },
    });
    dispatch({
      type: EExchangeActions.SET_LIMIT_VALUE,
      payload: { limitValue: undefined },
    });
    OrderActions.setOrderFlowState(
      EOrderFlowState.initial,
      orderContextDispatch,
      "ExchangeContext.resetLimitOrderState"
    );
  };

  useEffect(() => {
    if (availablePools?.[0]) {
      OrderActions.setPool(
        availablePools[0],
        orderDispatch,
        "ExchangeContext.useEffect.availablePools"
      );
    }
  }, [availablePools]);

  /**
   * Effect to update the `displayElements` and `displayImages` state
   */
  useEffect(() => {
    if (isLoading || isLoadingAvailablePools) return;

    if (
      Boolean(activeAssetAId && activeAssetBId && activeAssetAId !== activeAssetBId) &&
      isWalletConnected
    ) {
      setTimeout(() => {
        dispatch({
          type: EExchangeActions.SET_DISPLAY_ELEMENTS,
          payload: { displayElements: true },
        });
      }, contentAnimationTimeout);
    } else {
      dispatch({
        type: EExchangeActions.SET_DISPLAY_ELEMENTS,
        payload: { displayElements: false },
      });
    }
  }, [activeAssetAId, activeAssetBId, isLoading, isLoadingAvailablePools, isWalletConnected]);

  /**
   * This effect runs once the user changes the wallet or his ADA Balance changes.
   * This is to make sure we update the `isInsufficientA` / `isInsufficientB` state.
   */
  useEffect(() => {
    if (
      balanceLoaded &&
      !isLoading &&
      !isLoadingAvailablePools &&
      Boolean(state.valueAssetA) &&
      exchangeModeFromSearch.isSwap
    ) {
      checkInsufficientAssetA(state.valueAssetA);
    }
    if (balanceLoaded && !isLoading && Boolean(state.valueAssetB)) {
      checkInsufficientAssetB(state.valueAssetB);
    }
  }, [balanceLoaded, isLoading, availableAdaBalance]);

  return (
    <ExchangeContext.Provider
      value={{
        activeAssetA,
        activeAssetAId,
        activeAssetB,
        activeAssetBId,
        assetADecimals,
        assetBDecimals,
        assetsWithBalance,
        availablePools,
        balanceLoaded,
        checkInsufficientAssetA,
        checkInsufficientAssetB,
        dispatch,
        exchangeModeFromSearch,
        exchangeOrderTypeFromSearch,
        inputAssetList,
        isFetched: isFetched || isFetchedAvailablePools,
        isLoading: isLoading || isLoadingAvailablePools,
        isRefetching: isRefetching || isRefetchingAvailablePools,
        isValidLimitValue,
        isWalletConnected,
        outputAssetList,
        resetContext,
        resetLimitOrderState,
        setLimitValueToInitialRatio,
        state: {
          ...state,
          activePool, // Inherits from OrderContext
        },
      }}
    >
      {children}
    </ExchangeContext.Provider>
  );
};

// Utility hook.
export const useExchangeContext = (): IExchangeContext => useContext(ExchangeContext);
